import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
  EMPTY,
  Observable,
  Subscription,
  catchError,
  finalize,
  forkJoin,
  map,
  of,
  shareReplay,
  switchMap,
  tap
} from 'rxjs';
import { POSTHOG_KEYS } from 'src/app/constants/posthog-keys.const';
import { Plantation } from 'src/app/models/crd-state.interface';
import {
  PlotCurrentVersion,
  PlotReview
} from 'src/app/models/plot-review.model';
import { UserInfo } from 'src/app/models/user-state.interface';
import { DashboardService } from '../data-service/dashboard.service';
import { PosthogService } from '../posthog.service';
import { UtilityService } from '../utility.service';
import {
  DEFAULT_PLOT_VERSION,
  chunkPlotCodes,
  plotReviewKey
} from 'src/app/utils/plot-review.util';
import { UserStateService } from './user-state.service';

@Injectable({
  providedIn: 'root'
})
export class PlotReviewStateService {
  private readonly dashboardService = inject(DashboardService);
  private readonly userStateService = inject(UserStateService);
  private readonly utilityService = inject(UtilityService);
  private readonly translocoService = inject(TranslocoService);
  private readonly posthogService = inject(PosthogService);
  private readonly reviews = signal<ReadonlyMap<string, PlotReview>>(new Map());
  private readonly versions = signal<ReadonlyMap<string, number>>(new Map());
  private readonly saveGeneration = new Map<string, number>();
  private readonly inflightPuts = new Map<string, Observable<PlotReview>>();
  private loadSub?: Subscription;

  reviewFor(plantation: Plantation): PlotReview | null {
    const version = this.versions().get(plantation.plantation_code);
    if (version == null) {
      return null;
    }

    return (
      this.reviews().get(plotReviewKey(plantation.plantation_code, version)) ??
      null
    );
  }

  isReady(plantation: Plantation): boolean {
    return this.versions().has(plantation.plantation_code);
  }

  loadForPlantations(plantations: Plantation[]): void {
    this.loadSub?.unsubscribe();

    this.loadSub = this.posthogService
      .getFeatureFlag(POSTHOG_KEYS.PLOT_REVIEW)
      .pipe(
        switchMap((enabled) => {
          if (!enabled) {
            this.reviews.set(new Map());
            this.versions.set(new Map());
            return EMPTY;
          }

          const codes = [
            ...new Set(
              plantations
                .map((plantation) => plantation.plantation_code)
                .filter((code) => !!code)
            )
          ];

          const versionChunks = chunkPlotCodes(codes).map((chunk) =>
            this.dashboardService.getPlantationCurrentVersions(chunk).pipe(
              map((results) => this.versionsForChunk(chunk, results)),
              catchError(() => of(this.versionsForChunk(chunk, [])))
            )
          );

          const versions$ = versionChunks.length
            ? forkJoin(versionChunks).pipe(map((chunks) => chunks.flat()))
            : of([] as PlotCurrentVersion[]);

          return forkJoin({
            versions: versions$,
            reviews: this.dashboardService
              .getPlantationReviews()
              .pipe(catchError(() => of(null)))
          });
        })
      )
      .subscribe(({ versions, reviews }) => {
        if (versions.length > 0) {
          this.versions.update((current) => {
            const next = new Map(current);
            for (const item of versions) {
              next.set(item.plantation_code, item.version);
            }
            return next;
          });
        }

        if (reviews) {
          this.replaceReviews(reviews);
        }
      });
  }

  setReview(plantation: Plantation, status: boolean | null): void {
    const code = plantation.plantation_code;
    const cached = this.versions().get(code);
    const version$ =
      cached != null
        ? of(cached)
        : this.dashboardService.getPlantationCurrentVersions([code]).pipe(
            map((results) => results[0]?.version ?? DEFAULT_PLOT_VERSION),
            tap((version) => this.cacheVersion(code, version)),
            catchError(() => {
              this.cacheVersion(code, DEFAULT_PLOT_VERSION);
              return of(DEFAULT_PLOT_VERSION);
            })
          );

    version$.subscribe((version) => this.commit(code, version, status));
  }

  private commit(
    plantationCode: string,
    version: number,
    status: boolean | null
  ): void {
    const key = plotReviewKey(plantationCode, version);
    const previous = this.reviews().get(key) ?? null;
    const generation = (this.saveGeneration.get(key) ?? 0) + 1;
    this.saveGeneration.set(key, generation);

    this.writeLocal(
      key,
      this.optimisticReview(plantationCode, version, status, previous)
    );

    this.persist(key, plantationCode, version, status, previous)
      .pipe(
        catchError(() => {
          if (this.saveGeneration.get(key) === generation) {
            this.writeLocal(key, previous);
            this.utilityService.emitToast({
              isSuccess: false,
              message: this.translocoService.translate(
                'DASHBOARD.PLOT_REVIEW.SAVE_FAILED'
              )
            });
          }
          return EMPTY;
        })
      )
      .subscribe((response) => {
        if (this.saveGeneration.get(key) !== generation) {
          return;
        }

        this.writeLocal(key, response);
      });
  }

  private persist(
    key: string,
    plantationCode: string,
    version: number,
    status: boolean | null,
    previous: PlotReview | null
  ): Observable<PlotReview | null> {
    if (status !== null) {
      const put$ = this.dashboardService
        .putPlantationReview({
          plantation_code: plantationCode,
          version,
          status
        })
        .pipe(shareReplay({ bufferSize: 1, refCount: false }));

      this.inflightPuts.set(key, put$);
      return put$.pipe(
        finalize(() => {
          if (this.inflightPuts.get(key) === put$) {
            this.inflightPuts.delete(key);
          }
        })
      );
    }

    return this.idToDelete(key, previous).pipe(
      switchMap((id) =>
        id == null
          ? of(null)
          : this.dashboardService
              .deletePlantationReview(id)
              .pipe(map(() => null))
      )
    );
  }

  private idToDelete(
    key: string,
    previous: PlotReview | null
  ): Observable<number | null> {
    if (previous?.id != null) {
      return of(previous.id);
    }

    const pendingPut = this.inflightPuts.get(key);
    if (!pendingPut) {
      return of(null);
    }

    return pendingPut.pipe(
      map((review) => review.id ?? null),
      catchError(() => of(null))
    );
  }

  private optimisticReview(
    plantationCode: string,
    version: number,
    status: boolean | null,
    previous: PlotReview | null
  ): PlotReview | null {
    if (status === null) {
      return null;
    }

    const reviewed_by =
      previous?.reviewed_by ??
      (this.userStateService.getValue('userInfo') as UserInfo | null)?.contact
        ?.email ??
      '';

    return {
      ...(previous?.id != null ? { id: previous.id } : {}),
      plantation_code: plantationCode,
      version,
      status,
      reviewed_by,
      updated_at: new Date().toISOString()
    };
  }

  private replaceReviews(reviews: PlotReview[]): void {
    const next = new Map<string, PlotReview>();
    for (const review of reviews) {
      next.set(plotReviewKey(review.plantation_code, review.version), review);
    }
    this.reviews.set(next);
  }

  private cacheVersion(plantationCode: string, version: number): void {
    this.versions.update((current) => {
      const next = new Map(current);
      next.set(plantationCode, version);
      return next;
    });
  }

  private versionsForChunk(
    chunk: string[],
    results: PlotCurrentVersion[]
  ): PlotCurrentVersion[] {
    const byCode = new Map(
      results.map((item) => [item.plantation_code, item.version])
    );

    return chunk.map((plantation_code) => ({
      plantation_code,
      version: byCode.get(plantation_code) ?? DEFAULT_PLOT_VERSION
    }));
  }

  private writeLocal(key: string, review: PlotReview | null): void {
    this.reviews.update((current) => {
      const next = new Map(current);
      if (!review) {
        next.delete(key);
      } else {
        next.set(key, review);
      }
      return next;
    });
  }
}
